// ============================================================
// Wauvea WebXR — Dome Environment
// Skybox dome with spherical and minimalist mode support
// ============================================================

import * as THREE from 'three';
import { CONFIG } from '../config.js';

export class DomeEnvironment {
  constructor(scene) {
    this.scene = scene;
    this.skyMesh = null;
    this.mode = 'minimalist'; // 'spherical' | 'minimalist'
  }

  /**
   * Build the environment dome
   * @param {'spherical'|'minimalist'} mode
   */
  async build(mode = 'minimalist') {
    this.mode = mode;

    // Clean up previous build (prevents duplicate lights/ground on re-build)
    this._cleanup();

    if (mode === 'spherical') {
      await this._buildSphericalDome();
    } else {
      this._buildMinimalistDome();
    }

    this._buildGround();
    this._buildLighting();
  }

  _cleanup() {
    // Remove tagged objects from previous build
    const toRemove = [];
    this.scene.traverse(child => {
      if (child.userData._domeOwned) toRemove.push(child);
    });
    toRemove.forEach(obj => {
      this.scene.remove(obj);
      obj.geometry?.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
        else obj.material.dispose();
      }
    });
    if (this.skyMesh) {
      this.scene.remove(this.skyMesh);
      this.skyMesh.geometry?.dispose();
      this.skyMesh.material?.dispose();
      this.skyMesh = null;
    }
  }

  async _buildSphericalDome() {
    return new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      loader.load(
        CONFIG.dome.skyboxPath,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.mapping = THREE.EquirectangularReflectionMapping;

          const geo = new THREE.SphereGeometry(CONFIG.dome.radius, 64, 32);
          const mat = new THREE.MeshBasicMaterial({
            map: texture,
            side: THREE.BackSide,
          });

          this.skyMesh = new THREE.Mesh(geo, mat);
          this.skyMesh.name = 'skyDome';
          this.scene.add(this.skyMesh);
          this.scene.environment = texture;
          resolve();
        },
        undefined,
        () => {
          // Fallback to minimalist if skybox fails
          this._buildMinimalistDome();
          resolve();
        }
      );
    });
  }

  _buildMinimalistDome() {
    // Clean dark environment — Apple Vision Pro cinema-style
    this.scene.background = new THREE.Color(CONFIG.dome.ambientColor);

    // Subtle gradient sphere for depth
    const geo = new THREE.SphereGeometry(CONFIG.dome.radius, 32, 16);
    const mat = new THREE.MeshBasicMaterial({
      color: 0x0d0d20,
      side: THREE.BackSide,
    });

    this.skyMesh = new THREE.Mesh(geo, mat);
    this.skyMesh.name = 'skyDome';
    this.scene.add(this.skyMesh);
  }

  _buildGround() {
    const geo = new THREE.CircleGeometry(200, 64);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x060610,
      roughness: 0.95,
      metalness: 0.05,
      side: THREE.DoubleSide,
    });
    const ground = new THREE.Mesh(geo, mat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -2;
    ground.name = 'ground';
    ground.userData._domeOwned = true;
    this.scene.add(ground);
  }

  _buildLighting() {
    // Soft ambient fill
    const ambient = new THREE.AmbientLight(0x1a1a3a, 0.5);
    ambient.userData._domeOwned = true;
    this.scene.add(ambient);

    // Key light from above (subtle purple)
    const keyLight = new THREE.PointLight(0x6C5CE7, 1.2, 60);
    keyLight.position.set(0, 22, 0);
    keyLight.userData._domeOwned = true;
    this.scene.add(keyLight);

    // Fill light (subtle cyan)
    const fillLight = new THREE.PointLight(0x00D2FF, 0.5, 50);
    fillLight.position.set(-10, 10, -8);
    fillLight.userData._domeOwned = true;
    this.scene.add(fillLight);
  }

  setMode(mode) {
    if (this.skyMesh) {
      this.scene.remove(this.skyMesh);
      this.skyMesh.geometry?.dispose();
      this.skyMesh.material?.dispose();
      this.skyMesh = null;
    }
    this.build(mode);
  }
}
